import React from 'react';
import {
  X, RefreshCw, Upload, Mail, Phone, Calendar, User, Building,
  Trash2, FileText, Search, UserCheck, Download, Plus
} from 'lucide-react';
import QuoteClientCreate from './QuoteClientCreate';

// Rendu d'un champ de formulaire dynamique selon son type. Iso-comportement.
const DynamicFormField = ({ field, form }) => {
  const {
    t, clientId, formData, uploadedFiles, fileInputRefs,
    getTranslatedText, updateFormValue, handleFileSelect, removeFile, setFormData,
    clientSearchRef, clientSearchQuery, setClientSearchQuery, searchClients,
    clientSearchResults, setClientSearchResults, showClientDropdown, setShowClientDropdown,
    searchingClients, selectClient, selectedClient, setSelectedClient,
    showClientCreate, setShowClientCreate, backendUrl, authToken,
    companySearchRef, companySearchQuery, setCompanySearchQuery, filterCompanies,
    showCompanyDropdown, filteredCompanies, setFilteredCompanies, setShowCompanyDropdown,
    selectCompany, selectedCompany, setSelectedCompany, clientCompanies,
    siretLookupLoading, siretLookupError, lookupCompanySiret
  } = form;

  const value = formData[field.id] || '';
  const placeholder = getTranslatedText(field.placeholder, field.placeholder_en);

  switch (field.type) {
    case 'heading':
      return <h3 className="text-lg font-bold text-slate-800">{getTranslatedText(field.label, field.label_en)}</h3>;

    case 'first_name':
      return (
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            value={value}
            onChange={(e) => updateFormValue(field.id, e.target.value)}
            placeholder={placeholder || 'Prénom'}
            className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>
      );

    case 'last_name':
      return (
        <div className="relative">
          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            value={value}
            onChange={(e) => updateFormValue(field.id, e.target.value)}
            placeholder={placeholder || 'Nom'}
            className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>
      );

    case 'text_short':
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => updateFormValue(field.id, e.target.value)}
          placeholder={placeholder || getTranslatedText(field.label, field.label_en)}
          className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
        />
      );

    case 'text_long':
      return (
        <textarea
          value={value}
          onChange={(e) => updateFormValue(field.id, e.target.value)}
          placeholder={placeholder || getTranslatedText(field.label, field.label_en)}
          rows={3}
          className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500 resize-none"
        />
      );

    case 'email':
      return (
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="email"
            value={value}
            onChange={(e) => updateFormValue(field.id, e.target.value)}
            placeholder={placeholder || 'votre@email.com'}
            className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500"
          />
        </div>
      );

    case 'phone':
      return (
        <div className="relative">
          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="tel"
            value={value}
            onChange={(e) => updateFormValue(field.id, e.target.value)}
            placeholder={placeholder || '06 00 00 00 00'}
            className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500"
          />
        </div>
      );

    case 'dropdown':
      return (
        <select
          value={value}
          onChange={(e) => updateFormValue(field.id, e.target.value)}
          className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500"
        >
          <option value="">{placeholder || t('form.select')}</option>
          {field.options?.map(opt => (
            <option key={opt.id} value={opt.value}>
              {getTranslatedText(opt.label, opt.label_en)}
            </option>
          ))}
        </select>
      );

    case 'radio':
      return (
        <div className="space-y-2">
          {field.options?.map(opt => (
            <label
              key={opt.id}
              className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                value === opt.value
                  ? 'border-cyan-500 bg-cyan-50'
                  : 'border-slate-200 hover:border-cyan-300 bg-white'
              }`}
            >
              <input
                type="radio"
                name={field.id}
                value={opt.value}
                checked={value === opt.value}
                onChange={(e) => updateFormValue(field.id, e.target.value)}
                className="w-4 h-4 text-cyan-600"
              />
              <div className="flex items-center gap-2">
                {opt.icon === 'User' && <User size={18} className="text-cyan-600" />}
                {opt.icon === 'Building' && <Building size={18} className="text-cyan-600" />}
                <span className="font-medium text-slate-800">
                  {getTranslatedText(opt.label, opt.label_en)}
                </span>
              </div>
            </label>
          ))}
        </div>
      );

    case 'checkbox': {
      const checkboxValue = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-2">
          {field.options?.map(opt => (
            <label
              key={opt.id}
              className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                checkboxValue.includes(opt.value)
                  ? 'border-cyan-500 bg-cyan-50'
                  : 'border-slate-200 hover:border-cyan-300 bg-white'
              }`}
            >
              <input
                type="checkbox"
                value={opt.value}
                checked={checkboxValue.includes(opt.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFormValue(field.id, [...checkboxValue, opt.value]);
                  } else {
                    updateFormValue(field.id, checkboxValue.filter(v => v !== opt.value));
                  }
                }}
                className="w-4 h-4 text-cyan-600 rounded"
              />
              <span className="font-medium text-slate-800">
                {getTranslatedText(opt.label, opt.label_en)}
              </span>
            </label>
          ))}
        </div>
      );
    }

    case 'number':
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => updateFormValue(field.id, e.target.value)}
          placeholder={placeholder || '0'}
          min={field.validation?.min_value}
          max={field.validation?.max_value}
          className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500"
        />
      );

    case 'slider': {
      const sliderMin = field.config?.slider_min || 0;
      const sliderMax = field.config?.slider_max || 100;
      const sliderValue = value || sliderMin;
      return (
        <div className="space-y-2">
          <input
            type="range"
            min={sliderMin}
            max={sliderMax}
            step={field.config?.slider_step || 1}
            value={sliderValue}
            onChange={(e) => updateFormValue(field.id, parseInt(e.target.value))}
            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
          />
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">{sliderMin}</span>
            <span className="font-bold text-cyan-600">{sliderValue}</span>
            <span className="text-slate-500">{sliderMax}</span>
          </div>
        </div>
      );
    }

    case 'date':
      return (
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10">
            <Calendar size={18} />
          </div>
          <input
            type="date"
            value={value}
            onChange={(e) => updateFormValue(field.id, e.target.value)}
            style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
            className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500"
          />
        </div>
      );

    case 'file': {
      const fieldFiles = uploadedFiles[field.id] || [];
      return (
        <div className="space-y-3">
          <input
            type="file"
            ref={(el) => { fileInputRefs.current[field.id] = el; }}
            multiple
            onChange={(e) => handleFileSelect(field.id, e)}
            className="hidden"
            accept={field.config?.allowed_extensions?.map(ext => `.${ext}`).join(',') || '.pdf,.doc,.docx,.jpg,.jpeg,.png'}
          />
          <div
            onClick={() => fileInputRefs.current[field.id]?.click()}
            className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center bg-slate-50 hover:border-cyan-400 hover:bg-cyan-50 transition-colors cursor-pointer"
          >
            <Upload className="mx-auto mb-2 text-slate-400" size={32} />
            <p className="text-slate-600 text-sm font-medium">Cliquez pour ajouter des fichiers</p>
            <p className="text-slate-400 text-xs mt-1">PDF, DOC, JPG, PNG acceptés</p>
          </div>
          {fieldFiles.length > 0 && (
            <div className="space-y-2">
              {fieldFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-cyan-600" />
                    <span className="text-sm text-slate-700 truncate max-w-[200px]">{file.name}</span>
                    <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} Ko)</span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeFile(field.id, index); }}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    case 'file_download': {
      const cfg = field.config || {};
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!cfg.file_url) {
        return <p className="text-sm text-slate-400 italic">Aucun modèle disponible</p>;
      }
      const href = `${backendUrl}${cfg.file_url}${cfg.file_name ? `?name=${encodeURIComponent(cfg.file_name)}` : ''}`;
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          download
          data-testid={`file-download-${field.id}`}
          className="flex items-center gap-3 p-4 border-2 border-cyan-200 bg-cyan-50 rounded-xl hover:bg-cyan-100 transition-colors no-underline"
        >
          <div className="w-10 h-10 rounded-lg bg-cyan-600 flex items-center justify-center flex-shrink-0">
            <Download className="text-white" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{cfg.file_name || 'Modèle à télécharger'}</p>
            {cfg.file_size ? (
              <p className="text-xs text-slate-500">{(cfg.file_size / 1024).toFixed(1)} Ko</p>
            ) : null}
          </div>
          <span className="px-3 py-1.5 text-sm font-medium text-white bg-cyan-600 rounded-lg flex-shrink-0">
            Télécharger
          </span>
        </a>
      );
    }

    case 'client_search':
      return (
        <div className="space-y-3" ref={clientSearchRef}>          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10" size={18} />
            <input
              type="text"
              value={clientSearchQuery}
              onChange={(e) => {
                setClientSearchQuery(e.target.value);
                if (e.target.value.length >= 2) {
                  searchClients(e.target.value);
                } else {
                  setClientSearchResults([]);
                  setShowClientDropdown(false);
                }
              }}
              onFocus={() => {
                if (clientSearchResults.length > 0) {
                  setShowClientDropdown(true);
                }
              }}
              placeholder="Rechercher un client existant..."
              className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500"
            />
            {searchingClients && (
              <RefreshCw className="absolute right-4 top-1/2 -translate-y-1/2 text-cyan-500 animate-spin z-10" size={18} />
            )}
            {showClientDropdown && clientSearchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {clientSearchResults.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => selectClient(client)}
                    className="w-full px-4 py-3 text-left hover:bg-cyan-50 flex items-center gap-3 border-b border-slate-100 last:border-0"
                  >
                    <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                      <User size={20} className="text-cyan-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">
                        {client.first_name} {client.last_name}
                      </div>
                      <div className="text-sm text-slate-500">
                        {client.email || client.phone || 'Pas de contact'}
                      </div>
                    </div>
                    {client.admin_reference && (
                      <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                        {client.admin_reference}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedClient && (
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
              <UserCheck size={20} className="text-green-600" />
              <div className="flex-1">
                <span className="font-medium text-green-800">
                  Client sélectionné : {selectedClient.first_name} {selectedClient.last_name}
                </span>
                <p className="text-xs text-green-600">
                  Les informations ci-dessous sont pré-remplies. Vous pouvez les modifier si nécessaire.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedClient(null);
                  setClientSearchQuery('');
                  setFormData(prev => ({
                    ...prev,
                    field_nom: '',
                    field_prenom: '',
                    field_email: '',
                    field_telephone: '',
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    selected_client_id: null
                  }));
                }}
                className="text-green-600 hover:text-green-800"
              >
                <X size={18} />
              </button>
            </div>
          )}
          {showClientDropdown && clientSearchQuery.length >= 2 && clientSearchResults.length === 0 && !searchingClients && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
              {t('form.noClientFound')}
            </div>
          )}
          {!selectedClient && !showClientCreate && (
            <button
              type="button"
              onClick={() => setShowClientCreate(true)}
              className="flex items-center gap-2 text-sm font-medium text-cyan-700 hover:text-cyan-900"
              data-testid="quote-open-client-create"
            >
              <Plus size={16} /> Créer un nouveau client
            </button>
          )}
          {showClientCreate && !selectedClient && (
            <QuoteClientCreate
              backendUrl={backendUrl}
              authToken={authToken}
              onCancel={() => setShowClientCreate(false)}
              onSelected={(client) => {
                selectClient(client);
                setShowClientCreate(false);
              }}
            />
          )}
        </div>
      );

    case 'company_search':
      if (!clientId || clientCompanies.length === 0) {
        return null;
      }
      return (
        <div className="space-y-3" ref={companySearchRef}>
          <div className="relative">
            <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10" size={18} />
            <input
              type="text"
              value={companySearchQuery}
              onChange={(e) => {
                setCompanySearchQuery(e.target.value);
                filterCompanies(e.target.value);
              }}
              onFocus={() => {
                if (companySearchQuery.length >= 1) {
                  filterCompanies(companySearchQuery);
                } else if (clientCompanies.length > 0) {
                  setFilteredCompanies(clientCompanies);
                  setShowCompanyDropdown(true);
                }
              }}
              placeholder={t('form.searchCompany')}
              className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500"
            />
            {showCompanyDropdown && filteredCompanies.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {filteredCompanies.map((company) => (
                  <button
                    key={company.id}
                    type="button"
                    onClick={() => selectCompany(company)}
                    className="w-full px-4 py-3 text-left hover:bg-cyan-50 flex items-center gap-3 border-b border-slate-100 last:border-0"
                  >
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <Building size={20} className="text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">
                        {company.name}
                      </div>
                      <div className="text-sm text-slate-500">
                        {company.siret ? `SIRET: ${company.siret}` : company.city || t('form.noAddress')}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedCompany && (
            <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-xl">
              <Building size={20} className="text-orange-600" />
              <div className="flex-1">
                <span className="font-medium text-orange-800">
                  {t('form.companySelected')} : {selectedCompany.name}
                </span>
                <p className="text-xs text-orange-600">
                  {t('form.infoPreFilled')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedCompany(null);
                  setCompanySearchQuery('');
                  setFormData(prev => ({
                    ...prev,
                    field_entreprise: '',
                    field_siret: '',
                    field_adresse_pro: '',
                    entreprise: '',
                    nom_entreprise: '',
                    siret: '',
                    adresse_professionnelle: '',
                    adresse_pro: '',
                    selected_company_id: null
                  }));
                }}
                className="text-orange-600 hover:text-orange-800"
              >
                <X size={18} />
              </button>
            </div>
          )}
        </div>
      );

    case 'company_insee': {
      const sub = (k) => formData[`${field.id}_${k}`] || '';
      const setSub = (k, v) => updateFormValue(`${field.id}_${k}`, v);
      const siretClean = (sub('siret') || '').replace(/\s/g, '');
      const loading = siretLookupLoading === field.id;
      const lookupErr = (siretLookupError || {})[field.id];
      const inCreation = !!formData[`${field.id}_company_in_creation`];
      return (
        <div className="space-y-3">
          <div className="relative">
            <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10" size={18} />
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                value={sub('siret')}
                onChange={(e) => setSub('siret', e.target.value)}
                placeholder={placeholder || 'SIRET (14 chiffres)'}
                disabled={inCreation}
                className="flex-1 min-w-0 pl-12 pr-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500 disabled:bg-slate-50"
                data-testid={`company-insee-siret-${field.id}`}
              />
              <button
                type="button"
                onClick={() => lookupCompanySiret(field.id)}
                disabled={inCreation || loading || siretClean.length !== 14}
                className="px-4 py-3 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                data-testid={`company-insee-lookup-${field.id}`}
              >
                {loading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
                <span className="hidden sm:inline text-sm">{t('form.siretSearch')}</span>
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={inCreation}
              onChange={(e) => {
                updateFormValue(`${field.id}_company_in_creation`, e.target.checked);
                if (e.target.checked) setSub('siret', '');
              }}
              className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              data-testid={`company-insee-in-creation-${field.id}`}
            />
            <span className="text-sm text-slate-600">{t('form.companyInCreation')}</span>
          </label>
          {lookupErr && (
            <p className="text-sm text-red-600" data-testid={`company-insee-error-${field.id}`}>{lookupErr}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-500 mb-1">{t('form.companyName')}</label>
              <input type="text" value={sub('name')} onChange={(e) => setSub('name', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500" data-testid={`company-insee-name-${field.id}`} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-500 mb-1">{t('form.companyAddress')}</label>
              <input type="text" value={sub('address')} onChange={(e) => setSub('address', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">{t('form.companyPostal')}</label>
              <input type="text" value={sub('postal_code')} onChange={(e) => setSub('postal_code', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">{t('form.companyCity')}</label>
              <input type="text" value={sub('city')} onChange={(e) => setSub('city', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">{t('form.companyNaf')}</label>
              <input type="text" value={sub('naf')} onChange={(e) => setSub('naf', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">{t('form.companyEffectif')}</label>
              <input type="text" value={sub('effectif')} onChange={(e) => setSub('effectif', e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500" />
            </div>
          </div>
        </div>
      );
    }

    default:
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => updateFormValue(field.id, e.target.value)}
          placeholder={field.placeholder}
          className="w-full px-4 py-3 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500"
        />
      );
  }
};

export default DynamicFormField;
